import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button, IconButton, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import * as XLSX from 'xlsx';
import apiService from '../../services/api';

const ResponseTemplate = ({ surveyId, clientId }) => {
  const [surveyData, setSurveyData] = useState(null);
  const [responseData, setResponseData] = useState(null);
  const [questionTypes, setQuestionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  // 1. Get question type as lowercase
  const getQuestionType = (question, fetchedQuestionTypes) => {
    const found = fetchedQuestionTypes.find(type => type.ID === question.QuestionType);
    return found?.Type?.toLowerCase() || 'multiple choice';
  };

  // 2. Mapping options for multiple choice / checkboxes / dropdown
  const mapQuestionOptions = (questionOptions, matchingResponses) => {
    return questionOptions.map(opt => ({
      value: opt.OptionText || '',
      optionId: opt.OptionID || null,
      isSelected: matchingResponses.some(
        resp => resp.OptionID === opt.OptionID || resp.ResponseText === opt.OptionText
      ),
    }));
  };

  const mapResponseOptions = (matchingResponses) => {
    return matchingResponses.map(resp => ({
      value: resp.ResponseText || '',
      optionId: resp.OptionID || null,
      isSelected: true,
    }));
  };

  // 3. Map scale options for linear scale
  const mapScaleOptions = (question, matchingResponses) => {
    const scale = question.Scale?.[0] || {};
    const min = scale.MinValue || 1;
    const max = scale.MaxValue || 5;
    const selectedValue = Number(matchingResponses[0]?.ResponseText);

    return Array.from({ length: max - min + 1 }, (_, i) => ({
      value: min + i,
      isSelected: selectedValue === min + i,
    }));
  };

  const mapOptions = (question, matchingResponses, questionType) => {
    if (!['multiple choice', 'checkboxes', 'dropdown'].includes(questionType)) return [];

    if (Array.isArray(question.Options)) {
      return mapQuestionOptions(question.Options, matchingResponses);
    } else {
      return mapResponseOptions(matchingResponses);
    }
  };

  // 4. Map question
  const mapQuestion = (question, fetchedQuestionTypes, responseResults) => {
    const questionType = getQuestionType(question, fetchedQuestionTypes);
    const matchingResponses = responseResults.filter(resp => resp.QuestionID === question.QuestionID);

    return {
      questionId: question.QuestionID || null,
      text: question.QuestionText || 'No question text',
      options: questionType === 'linear scale'
        ? mapScaleOptions(question, matchingResponses)
        : mapOptions(question, matchingResponses, questionType),
      questionType,
      addCommentBox: question.QuestionComments || false,
      userResponse: matchingResponses[0]?.ResponseText || '',
      comment: matchingResponses[0]?.Comments || '',
      min: question.Scale?.[0]?.MinValue || 1,
      max: question.Scale?.[0]?.MaxValue || 5,
      minLabel: question.Scale?.[0]?.MinValueLabel || '',
      maxLabel: question.Scale?.[0]?.MaxValueLabel || '',
    };
  };

  const mapSubsection = (subsection, sIdx, fetchedQuestionTypes, responseResults) => ({
    measureId: subsection.SubSectionID || `measure-${sIdx}`,
    measureName: subsection.SubSectionName || `Measure ${sIdx + 1}`,
    measureDescription: subsection.SubSectionDescription || '',
    questions: Array.isArray(subsection.Questions)
      ? subsection.Questions.map(q => mapQuestion(q, fetchedQuestionTypes, responseResults))
      : [],
  });

  const mapSection = (section, fetchedQuestionTypes, responseResults) => ({
    sectionId: section.SectionID || `section-${crypto.randomUUID()}`,
    title: section.SectionName || 'Untitled Section',
    description: section.SectionDescription || '',
    measures: Array.isArray(section.SubSections)
      ? section.SubSections.map((sub, idx) => mapSubsection(sub, idx, fetchedQuestionTypes, responseResults))
      : [],
  });

  // Function to format data for Excel export
  const exportToExcel = () => {
    if (!surveyData || !surveyData.sections) return;

    const data = [];
    
    surveyData.sections.forEach(section => {
      section.measures.forEach(measure => {
        measure.questions.forEach(question => {
          const responseText = question.questionType === 'multiple choice' || 
                             question.questionType === 'checkboxes' || 
                             question.questionType === 'dropdown'
            ? question.options
                .filter(option => option.isSelected)
                .map(option => option.value)
                .join(', ')
            : question.questionType === 'linear scale'
              ? question.options.find(option => option.isSelected)?.value || ''
              : question.userResponse || 'No response provided';

          data.push({
            'Section': section.title,
            'Measure': measure.measureName,
            'Question': question.text,
            'Question Type': question.questionType,
            'Response': responseText,
            'Comment': question.comment || 'No comment provided',
          });
        });
      });
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Survey Responses');
    
    // Auto-size columns
    const colWidths = [
      { wch: 30 }, // Section
      { wch: 30 }, // Measure
      { wch: 50 }, // Question
      { wch: 20 }, // Question Type
      { wch: 50 }, // Response
      { wch: 50 }, // Comment
    ];
    worksheet['!cols'] = colWidths;

    XLSX.writeFile(workbook, `${surveyData.name || 'Survey'}_Responses.xlsx`);
  };

  useEffect(() => {
    const fetchSurveyDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('Fetching with surveyId:', surveyId, 'clientId:', clientId);

        let fetchedQuestionTypes = [];
        try {
          const questionTypesData = await apiService.fetchData('questions_types');
          fetchedQuestionTypes = Array.isArray(questionTypesData.results) ? questionTypesData.results : [];
          setQuestionTypes(fetchedQuestionTypes);
        } catch (err) {
          console.warn('Failed to fetch question types:', err.message, err.response);
        }

        let surveyResult = {};
        try {
          const survey = await apiService.fetchData(`surveys/${surveyId}`);
          surveyResult = Array.isArray(survey.results) ? survey.results[0] : survey.results || {};
        } catch (err) {
          console.error('Failed to fetch survey:', err.message, err.response);
          throw new Error(`Failed to fetch survey data: ${err.message}`);
        }

        let responseResults = [];
        try {
          const responses = await apiService.fetchData(`response/${clientId}`);
          responseResults = Array.isArray(responses.results) ? responses.results : [];
          setResponseData(responseResults);
        } catch (err) {
          console.error('Failed to fetch responses:', err.message, err.response);
          throw new Error(`Failed to fetch response data: ${err.message}`);
        }

        console.log('Survey Data:', surveyResult);
        console.log('Response Data:', responseResults);
        console.log('Question Types:', fetchedQuestionTypes);

        const formattedSections = Array.isArray(surveyResult.Sections)
          ? surveyResult.Sections.map(section =>
              mapSection(section, fetchedQuestionTypes, responseResults)
            )
          : [];

        if (formattedSections.length === 0) {
          console.warn('No sections found in survey data');
        }

        setSurveyData({
          name: surveyResult.Name || 'Untitled Survey',
          sections: formattedSections,
        });

        setExpandedSections(formattedSections.reduce((acc, section) => ({
          ...acc,
          [section.sectionId]: true,
        }), {}));

        setLoading(false);
      } catch (err) {
        console.error('Error fetching survey details:', err.message, err.response);
        setError(`Failed to load survey or response details: ${err.message}`);
        setLoading(false);
      }
    };

    if (surveyId && clientId) {
      fetchSurveyDetails();
    } else {
      setError('Missing surveyId or clientId.');
      setLoading(false);
    }
  }, [surveyId, clientId]);

  const toggleSectionVisibility = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const renderQuestionInput = (question) => {
    const renderCommentBox = () => (
      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, mb: 0.5, display: 'block' }}>
          Comment:
        </Typography>
        <Box
          sx={{
            p: 1.5,
            backgroundColor: '#fff',
            border: '1px solid #d0d0d0',
            borderRadius: 1,
            color: question.comment ? '#333' : '#999',
            fontStyle: question.comment ? 'normal' : 'italic'
          }}
        >
          {question.comment || 'No comment provided'}
        </Box>
      </Box>
    );

    const renderOptions = () => (
      <div className="survey-form-options-list" style={{ width: '100%' }}>
        {question.options && question.options.length > 0 ? (
          question.options.map((option, optionIndex) => (
            <div
              key={option.optionId || option.value || optionIndex}
              className={
                question.questionType === 'multiple choice'
                  ? 'survey-form-radio-group'
                  : question.questionType === 'checkboxes'
                    ? 'survey-form-checkbox-group'
                    : 'survey-form-dropdown-group'
              }
              style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}
            >
              {question.questionType === 'multiple choice' && (
                <input
                  type="radio"
                  name={`question-${question.questionId}`}
                  value={option.value}
                  checked={option.isSelected}
                  readOnly
                  style={{ marginRight: '8px' }}
                />
              )}
              {question.questionType === 'checkboxes' && (
                <input
                  type="checkbox"
                  value={option.value}
                  checked={option.isSelected}
                  readOnly
                  style={{ marginRight: '8px' }}
                />
              )}
              {question.questionType === 'dropdown' && (
                <span style={{ marginRight: '8px' }}>{optionIndex + 1}. </span>
              )}
              <input
                type="text"
                value={option.value}
                readOnly
                className="survey-details-option-input"
                style={{
                  flex: 1,
                  marginRight: '8px',
                  width: '700px',
                  backgroundColor: question.questionType === 'dropdown' && option.isSelected ? '#d0e7ff' : 'transparent',
                  border: question.questionType === 'dropdown' && option.isSelected ? '1px solid #007bff' : '1px solid #ccc',
                  borderRadius: '4px',
                  padding: '4px 8px',
                }}
              />
            </div>
          ))
        ) : (
          <Typography>No options available.</Typography>
        )}
      </div>
    );

    switch (question.questionType) {
      case 'multiple choice':
      case 'checkboxes':
      case 'dropdown':
        return (
          <>
            <div className="survey-form-form-row survey-form-option-row">{renderOptions()}</div>
            {question.addCommentBox && renderCommentBox()}
          </>
        );
      case 'short answer':
        return (
          <>
            <Box sx={{ p: 1.5, backgroundColor: '#fff', border: '1px solid #d0d0d0', borderRadius: 1 }}>
              <Typography sx={{ color: question.userResponse ? '#333' : '#999', fontStyle: question.userResponse ? 'normal' : 'italic' }}>
                {question.userResponse || 'No response provided'}
              </Typography>
            </Box>
            {question.addCommentBox && renderCommentBox()}
          </>
        );
      case 'paragraph':
        return (
          <>
            <Box sx={{ p: 1.5, backgroundColor: '#fff', border: '1px solid #d0d0d0', borderRadius: 1, minHeight: '80px' }}>
              <Typography sx={{ color: question.userResponse ? '#333' : '#999', fontStyle: question.userResponse ? 'normal' : 'italic', whiteSpace: 'pre-wrap' }}>
                {question.userResponse || 'No response provided'}
              </Typography>
            </Box>
            {question.addCommentBox && renderCommentBox()}
          </>
        );
      case 'linear scale':
        const hasLabels = question.minLabel || question.maxLabel;
        return (
          <>
            <div className="survey-form-linear-scale-group">
              <div
                className="survey-form-linear-scale-range"
                style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
              >
                <div style={{ display: 'flex', gap: '10px' }}>
                  {question.options.map(option => (
                    <div
                      key={option.value}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                    >
                      <input
                        type="radio"
                        name={`question-${question.questionId}`}
                        value={option.value}
                        checked={option.isSelected}
                        readOnly
                        style={{ marginBottom: '5px' }}
                      />
                      <span>{option.value}</span>
                    </div>
                  ))}
                </div>
              </div>
              {hasLabels && (
                <div>
                  {/* Labels for linear scale */}
                </div>
              )}
            </div>
            {question.addCommentBox && renderCommentBox()}
          </>
        );
      default:
        return <Typography color="error">Error: Unrecognized question type "{question.questionType}".</Typography>;
    }
  };

  if (!surveyId || !clientId) {
    return <Typography color="error">Missing surveyId or clientId.</Typography>;
  }

  return (
    <Box sx={{ p: 2 }}>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} />
        </Box>
      )}
      {error && <Typography color="error">{error}</Typography>}
      {surveyData && (
        <div className="survey-details-questions-tab">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, pb: 2, borderBottom: '2px solid #e0e0e0' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#013E7D' }}>
              {surveyData.name}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={exportToExcel}
              disabled={loading || !surveyData}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Download Response
            </Button>
          </Box>
          {surveyData.sections.length > 0 ? (
            surveyData.sections.map((section, idx) => (
              <Box
                key={section.sectionId}
                sx={{
                  mb: 3,
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    backgroundColor: '#f8f9fa',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#e9ecef' }
                  }}
                  onClick={() => toggleSectionVisibility(section.sectionId)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton size="small" sx={{ color: '#013E7D' }}>
                      {expandedSections[section.sectionId] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#013E7D' }}>
                      {section.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                    Section {idx + 1} of {surveyData.sections.length}
                  </Typography>
                </Box>
                {expandedSections[section.sectionId] && (
                  <Box sx={{ p: 3, backgroundColor: '#fff' }}>
                    {section.description && (
                      <Typography sx={{ mb: 2, color: '#555', fontStyle: 'italic' }}>
                        {section.description}
                      </Typography>
                    )}
                    <div className="add-survey-questions-list" id="default">
                      {section.measures.length > 0 ? (
                        section.measures.map((measure, mIdx) => (
                          <Box key={measure.measureId} sx={{ mb: 3, pl: 2 }}>
                            <Typography variant="subtitle1" sx={{ color: '#013E7D', fontWeight: 600, mb: 1 }}>
                              {measure.measureName || `Measure ${mIdx + 1}`}
                            </Typography>
                            {measure.measureDescription && (
                              <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                                {measure.measureDescription}
                              </Typography>
                            )}
                            <div className="add-survey-questions-list" style={{ marginTop: '10px' }}>
                              {measure.questions.length > 0 ? (
                                measure.questions.map((q, qIdx) => (
                                  <Box
                                    key={q.questionId}
                                    sx={{
                                      mb: 2,
                                      p: 2,
                                      backgroundColor: '#f9f9f9',
                                      borderRadius: 1,
                                      border: '1px solid #e0e0e0'
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                                      <Typography sx={{ fontWeight: 600, color: '#013E7D', minWidth: '30px' }}>
                                        {qIdx + 1}.
                                      </Typography>
                                      <Typography sx={{ flex: 1, fontWeight: 500 }}>{q.text}</Typography>
                                    </Box>
                                    {renderQuestionInput(q)}
                                  </Box>
                                ))
                              ) : (
                                <Typography sx={{ color: '#999' }}>No questions in this measure.</Typography>
                              )}
                            </div>
                          </Box>
                        ))
                      ) : (
                        <Typography sx={{ color: '#999' }}>No measures in this section.</Typography>
                      )}
                    </div>
                  </Box>
                )}
              </Box>
            ))
          ) : (
            <Typography sx={{ color: '#999', textAlign: 'center', mt: 4 }}>No sections available for this survey.</Typography>
          )}
        </div>
      )}
    </Box>
  );
};

ResponseTemplate.propTypes = {
  surveyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default ResponseTemplate;